// noinspection ES6MissingAwait
import { dehydrate } from '@tanstack/react-query';
import { getQueryClient, redirect } from 'common-nextjs';
import { KnownCookies } from 'common-types';
import { fetcher, getBaseUrl } from 'fetcher-session';
import { NextPageContext } from 'next';
import { parseCookies } from 'nookies';
import { qsParse, qsStringify } from 'utils';
import { fetchSortConfig } from '~/api/configuration';
import { fetchItemById } from '~/api/item';
import { fetchFacetItems } from '~/api/items';
import {
  fetchLandersBySlug,
  fetchSuggestedLanderById,
  fetchV2LanderData,
} from '~/api/landers';
import { fetchUserSearchFilters, reduceEnabledSearchFilters } from '~/api/user';
import { LinkModuleLink } from '~/app/api/link-modules/route';
import { RESULTS_PAGE_SIZE } from '~/constants/resultsPageSize';
import { fetchButterModelDescription } from '~/lib/buttercms/model-description/api';
import type { ShopLanderPageProps } from '~/pages/shop/[...lander]';
import { RailsFacetQueryOptions } from '~/typings/services/rails/facets';
import deepmergeWithArrays from '~/utils/deepmergeWithArrays';
import doesFilterStateContainADetail from '~/utils/doesFilterStateContainADetail';
import getLanderId from '~/utils/getLanderId';

export async function fetchShopPageV2Lander(
  ctx: NextPageContext,
): Promise<ShopLanderPageProps | { notFound: true }> {
  const { lander: baseLanderArray } = ctx.query as RailsFacetQueryOptions;

  const splitAsPath = ctx.asPath?.split('?');
  const query = qsParse(splitAsPath?.[1]) as RailsFacetQueryOptions;

  const queryClient = getQueryClient();

  const landerId = getLanderId(baseLanderArray);

  const [sorts, userSearchFilters, lander, v2LanderData] = await Promise.all([
    fetchSortConfig(query.q ? 'naked_query_results' : undefined),
    fetchUserSearchFilters.call(ctx).catch(() => undefined),
    (async () => {
      if (landerId) {
        return fetchSuggestedLanderById.call(ctx, landerId);
      } else {
        return (
          await fetchLandersBySlug.call(ctx, baseLanderArray.join('/'))
        )[0];
      }
    })(),
    landerId ? fetchV2LanderData(landerId, queryClient) : null,
  ] as const);

  if (lander.type === 'navigation') {
    throw await redirect(ctx, `/nav/[...lander]`, {
      as: `/nav/${lander.path}`,
      statusCode: 301,
      replace: true,
    });
  }

  if (lander.type === 'model') {
    // @ts-ignore
    ctx.res?.status?.(404);
    throw new Error('Something went wrong');
  }

  if (ctx.asPath && !splitAsPath?.[0].endsWith(lander.path)) {
    const stringified = qsStringify(query);

    throw await redirect(ctx, `/shop/[...lander]${stringified}`, {
      as: `/shop/${lander.path}${stringified}`,
      statusCode: 301,
      shallow: true,
      replace: true,
    });
  }

  const cookies: KnownCookies = parseCookies(ctx);

  const originalQuery: Record<string, any> = deepmergeWithArrays([
    lander.query,
    query,
    reduceEnabledSearchFilters(
      userSearchFilters,
      cookies.fast_shipping_filter_enabled,
      cookies.canadian_sellers_filter_enabled,
    ),
    {
      page_size: RESULTS_PAGE_SIZE,
      context: query.q ? 'naked_query_results' : undefined,
      id: undefined,
    },
  ]);

  if (doesFilterStateContainADetail(originalQuery)) {
    originalQuery.include_model = 1;
  }

  const [response, similarToItem, _, trendingSearches] = await Promise.all([
    fetchFacetItems.call(ctx, originalQuery).catch(null),
    (async () => {
      if (query.similar_to) {
        try {
          return fetchItemById.call(ctx, query.similar_to);
        } catch (e) {}
      }
    })(),
    (async () => {
      if (lander.model) {
        try {
          await queryClient.prefetchQuery(
            ['butter-model-description', lander.id],
            () =>
              fetchButterModelDescription(
                `model-${lander.model?.id}`,
                query.preview == '1',
              ),
          );
        } catch (e) {}
      }
    })(),
    (async () => {
      if (lander.model) {
        try {
          return await fetcher<LinkModuleLink[]>('/api/link-modules', {
            baseUrl: getBaseUrl('self'),
            params: {
              type: 'model-lander',
              id: lander.id,
            },
          });
        } catch (e) {
          console.log('Error fetching trending searches', e);
        }
      }
    })(),
  ]);

  let notFoundMetadata: ShopLanderPageProps['notFoundMetadata'];

  // On model landers with 0 results, show results from just the category + brand results
  if (lander?.model && response.data?.length === 0) {
    const newResponse = await fetchFacetItems.call(
      ctx,
      deepmergeWithArrays([
        {
          category: lander.query.category,
          brand: lander.query.brand,
          page_size: 24,
        },
        query,
        reduceEnabledSearchFilters(
          userSearchFilters,
          cookies.fast_shipping_filter_enabled,
          cookies.canadian_sellers_filter_enabled,
        ),
        {
          id: undefined,
          context: query.q ? 'naked_query_results' : undefined,
        },
      ]),
    );

    // Only return 24 results
    response.data = newResponse.data.slice(0, 24);
    // response.meta = newResponse.meta;
    response.meta.paging.total_count = 24;

    // Cut out model + landerId from path, and include user params
    const newUrl =
      lander.path.split('/').slice(0, -2).join('/') + qsStringify(query);

    notFoundMetadata = {
      originalQuery: lander.query,
      title: lander.model.brand_name + ' ' + lander.model.category_name,
      url: `/shop/${newUrl}`,
    };
  }

  // id is added for Visited Results consistency
  const filterState = {
    ...response.meta.query,
    stid: query.stid,
    id: Math.random(),
  };

  return {
    filterState,
    lander,
    notFoundMetadata,
    pageView: v2LanderData?.page_view!,
    pageViewData: [],
    similarToItem,
    sorts,
    userSearchFilters,
    trendingSearches,
    facets: response.meta.facets,
    items: response.data,
    paging: response.meta.paging,
    dehydratedState: dehydrate(queryClient),
  };
}
